import {
  Col,
  Form,
  type FormInstance,
  Input,
  Modal,
  Row,
  Select,
  message,
  type InputRef
} from 'antd'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { client } from '~/api/base'
import { DatePicker } from '~/components/DatePicker'
import { type CouponCode } from '~/types/coupon-codes'
import { showErrors } from '~/utils/helpers'
import { useUpcomingCoursesQuery } from '../Courses/query'

interface Props {
  coupon: CouponCode | null
  open: boolean
  onClose: () => void
}

export function CouponFormModal({ coupon, open, onClose }: Props) {
  const isUpdate = coupon != null
  const { data: courses } = useUpcomingCoursesQuery()
  const mutation = useMutation({
    mutationFn: async (values: any) => {
      if (isUpdate) {
        return await client.put(`api/coupon-codes/${coupon.id}`, values)
      }

      return await client.post('api/coupon-codes', values)
    },
    onError: showErrors,
    onSuccess: () => {
      message.success('Success')
      onClose()
    }
  })

  const firstInputRef = useRef<InputRef>(null)
  const formRef = useRef<FormInstance>(null)

  const onSubmit = useCallback(async () => {
    const values = await formRef.current?.validateFields()
    mutation.mutate(values)
  }, [mutation])

  useEffect(() => {
    setTimeout(() => firstInputRef.current?.focus(), 300)
  }, [])

  const defaults = useMemo(() => {
    if (coupon != null) {
      return {
        ...coupon,
        expiry_date:
          coupon.expiry_date == null ? undefined : new Date(coupon.expiry_date)
      }
    }
    return { discount_type: 'fixed', activated: true }
  }, [coupon])

  return (
    <Modal
      open={open}
      width={720}
      afterClose={onClose}
      destroyOnHidden
      onOk={() => {
        onSubmit()
      }}
      onCancel={onClose}
      confirmLoading={mutation.isPending}
    >
      <Form
        ref={formRef}
        layout='vertical'
        preserve={false}
        initialValues={defaults}
      >
        <Row>
          <Col span={24}>
            <Form.Item name='code' label='Code' rules={[{ required: true }]}>
              <Input placeholder='Code' ref={firstInputRef} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name='description'
              label='Description'
              rules={[{ required: true }]}
            >
              <Input.TextArea placeholder='Description' />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name='discount_type'
              label='Discount type'
              rules={[
                { required: true, message: 'Please select the location' }
              ]}
            >
              <Select options={[{ value: 'fixed' }, { value: 'percentage' }]} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name='value'
              label='Discount amount'
              rules={[{ required: true }]}
            >
              <Input placeholder='Amount' />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item name='usage_limit' label='Usage limit'>
              <Input type='number' placeholder='Unlimited usage' />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name='event_id'
              label='Course'
              rules={[{ required: true }]}
            >
              <Select
                placeholder='Course'
                disabled={isUpdate}
                options={courses?.map((i) => ({
                  value: i.id,
                  label: i.name
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name='expiry_date'
              label={
                'Expiry date (Timezone: ' +
                Intl.DateTimeFormat().resolvedOptions().timeZone +
                ')'
              }
            >
              <DatePicker
                style={{ width: '100%' }}
                format='DD MMMM, YYYY HH:mm'
                showTime
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name='activated'
              label='Status'
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  {
                    value: true,
                    label: 'Activated'
                  },
                  {
                    value: false,
                    label: 'Inactivated'
                  }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
